import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ConnectionProvider,
  WalletProvider as SolanaWalletProvider,
  useWallet,
} from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { WalletError } from '@solana/wallet-adapter-base';
import { clusterApiUrl } from '@solana/web3.js';
import { useClusterStore } from '../state/clusterStore';
import { http } from '../api/http';
import { toast } from 'sonner';

/** Wallets temporarily blocked from connecting. */
const DISABLED_WALLETS = new Set(['Phantom']);

// Clear any disabled wallet from localStorage BEFORE the adapter mounts,
// so autoConnect doesn't try to reconnect to a blocked wallet.
if (typeof window !== 'undefined') {
  const saved = localStorage.getItem('walletName');
  if (saved && DISABLED_WALLETS.has(saved)) {
    localStorage.removeItem('walletName');
  }
}

interface Props {
  children: React.ReactNode;
}

const getLocalEndpoint = (cluster: 'devnet' | 'testnet' | 'mainnet-beta'): string => {
  if (cluster === 'mainnet-beta') {
    return import.meta.env.VITE_MAINNET_RPC_URL || clusterApiUrl(cluster);
  }
  if (cluster === 'devnet') {
    return (
      import.meta.env.VITE_SOLANA_RPC_DEVNET ||
      import.meta.env.VITE_DEVNET_RPC_URL ||
      clusterApiUrl(cluster)
    );
  }
  return clusterApiUrl(cluster);
};

/**
 * Auto-disconnects if the user selects a disabled wallet (e.g. Phantom).
 * Must render inside SolanaWalletProvider to access useWallet().
 */
const DisabledWalletBlocker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { wallet, disconnect } = useWallet();

  useEffect(() => {
    if (wallet && DISABLED_WALLETS.has(wallet.adapter.name)) {
      disconnect().catch(() => {});
      toast.error('Phantom is temporarily unavailable. Please use Jupiter or Solflare.');
    }
  }, [wallet, disconnect]);

  return <>{children}</>;
};

export const WalletProvider: React.FC<Props> = ({ children }) => {
  const { cluster } = useClusterStore();
  const [endpoint, setEndpoint] = useState<string | null>(null);

  // Fetch the RPC endpoint from the server BEFORE rendering wallet providers.
  // This avoids an endpoint swap after the wallet connects, which causes
  // Phantom to reject transactions with "User rejected the request".
  useEffect(() => {
    let cancelled = false;
    setEndpoint(null);
    http
      .get<{ rpcEndpoint: string }>(`/config?cluster=${cluster}`)
      .then((data) => {
        if (!cancelled && data.rpcEndpoint) {
          setEndpoint(data.rpcEndpoint);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEndpoint(getLocalEndpoint(cluster));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cluster]);

  // Empty array — wallets that support the Wallet Standard protocol
  // (Phantom, Solflare, Backpack, etc.) are auto-detected and should NOT
  // be added here. Adding them causes dual-registration conflicts where
  // signMessage and sendTransaction fail with "User rejected the request".
  const wallets = useMemo(() => [], []);

  const autoConnect = true;

  const onError = useCallback((error: WalletError) => {
    console.error('[Wallet Error]', error);
    toast.error(error.message || 'Wallet connection failed');
  }, []);

  // Wait for the endpoint to resolve before mounting wallet providers
  if (!endpoint) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-arcade-cyan border-t-transparent" />
      </div>
    );
  }

  return (
    <ConnectionProvider endpoint={endpoint} config={{ commitment: 'confirmed' }}>
      <SolanaWalletProvider wallets={wallets} autoConnect={autoConnect} onError={onError}>
        <WalletModalProvider>
          <DisabledWalletBlocker>{children}</DisabledWalletBlocker>
        </WalletModalProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  );
};
